import PortersGenericStrategiesImg from "lib/img/concepts/Differentiation_Porters_Generic_Strategies.png";
import Wedges from "../components/Wedge";
import { Concepts } from "lib/data/Concepts";
import { Image, RelatedFrameworks, H1, H2, Ol, P, Ul, Updated } from "design_lib";
export default function PortersGenericStrategies() {
  return (
    <div>
      <P>
        Cost | Differentiation | Features | Convenience | Brand | Quality | Product Quality |
        Customer Service | Jobs to be Done | Consumption Chain | Strategy Canvas | Buyer Utility Map
      </P>
      <H1>Definition/Description</H1>

      <P>
        Michael Porter’s Generic Strategies matrix is one of the foundational building blocks of
        Strategy. It has four quadrants: Cost Leadership, Focused Cost, Differentiation, and Focused
        Differentiation. The left side of the matrix focuses on lowering costs while the right side
        focuses on differentiating:
      </P>

      <Image src={PortersGenericStrategiesImg} alt="" />
      <P>
        These generic strategies categorize firms on how they provide value to customers. Cost
        increases the value proposition by lowering costs while Differentiation increases the value
        proposition by raising the customer’s willingness to pay, as shown through the value stick
        below:
      </P>
      <div className="d-flex flex-wrap gap-3 justify-content-between">
        <Wedges />
      </div>
      <P>
        More successful companies are positioned in just one of these quadrants. Companies that span
        multiple quadrants are often mediocre in their efforts to be too many things and add less
        value in the customers' eyes.
      </P>
      <H1>Cost</H1>
      <P>
        Having a cost advantage means that a company’s costs are lower than their competition. While
        this does mean that often companies will lower prices when they have a cost advantage, a
        cost advantage does not come from price. A cost advantage comes from lower costs. Within
        Porter’s Generic Strategies Matrix, Cost has two categories: Cost Leadership and Focused
        Cost. Cost Leadership is to have the lowest costs while serving multiple segments in an
        industry. Focused Cost is to have lower costs within a segment of the market. There are many
        ways to obtain a cost advantage. Some of the most important drivers of cost advantage are:
      </P>
      <Ol>
        <li>Economies of Scope</li>
        <li>Economies of Scale</li>
        <li>Learning/Experience Curves</li>
        <li>Different Business Model/Value Chain</li>
        <li>Lowering Input Costs</li>
      </Ol>
      <P>
        The best Cost Advantages use a combination of these tools. Refer to the Cost article for
        more information.
      </P>
      <P>
        *Economies of Scope, Economies of Scale, Learning/Experience Curves, and Cost are
        hyperlinked to their respective articles and Different Business Model/Value Chain is
        hyperlinked to the Innovation Strategies article*
      </P>
      <H1>Differentiation</H1>

      <P>
        To differentiate means to do something different from the competition which creates value
        for customers and raises their willingness to pay. Within the Generic Strategies matrix,
        Differentiation has two categories: Differentiation and Focused Differentiation.
        Differentiation is to differentiate across multiple segments of the industry. Focused
        Differentiation creates unique value for a specific aspect of the market. There are two
        sub-categories of the tool of Differentiation:
      </P>
      <Ol>
        <li>Product</li>
        <li>Customer Interaction</li>
      </Ol>
      <H1>Cost Examples</H1>
      <H2>Cost Leadership</H2>
      <P>
        Walmart – Walmart focuses on low costs. It is able to keep its costs low due to intense
        negotiations with suppliers, efficient distribution and no-frills stores. Due to lower
        costs, Walmart can offer lower prices on a wide variety of goods and gain Cost Leadership.
      </P>
      <H2>Focused Cost</H2>
      <P>
        RyanAir- RyanAir is known for its low costs for the price sensitive customer segment of the
        market. RyanAir outsources customer service and utilizes only one type of plane to lower
        maintenance and employee training costs. They also restrict the maximum hours an employee
        can work to avoid paying overtime and utilize point-to-point flights which allows them to
        have quick turnaround times and maximize the amount of people they can service per day.
        These cost cutting procedures come together to help them have lower fares than most
        airlines. With lower costs, they can offer lower fares that attract price sensitive
        customers.
      </P>
      <H1>Differentiation Examples</H1>
      <H2>Differentiation</H2>
      <P>
        Tesla- Tesla differentiates itself in the automotive industry by combining cutting-edge
        electric technology with high performance. Their focus on sustainability, along with
        features like Autopilot and over-the-air software updates, offers a unique selling point
        that traditional car manufacturers do not offer. They are classified as Differentiation
        because their EV cars are marketed to everyone in the EV market.
      </P>
      <H2>Focused Differentiation</H2>
      <P>
        Ferrari- Ferrari focuses on a niche part of the automotive industry; the rich people who
        want fast and luxurious cars. They are able to differentiate themselves through their strong
        brand image that comes from owning a Ferrari. For this reason, they have dominated the
        sports car market.
      </P>
      <RelatedFrameworks
        concepts={[
          Concepts.ExperienceCurve,
          Concepts.JobsToBeDoneConcept,
          Concepts.ConsumptionChain,
          Concepts.StrategyCanvas,
        ]}
        unfinishedConcepts={[
          "Innovation Strategies",
          "Economies of Scope",
          "Economies of Scale",
          "Buyer Utility Map",
        ]}
      />
      <H2>History</H2>

      <P>
        Porter’s Generic Strategies was initially conceptualized by Harvard professor Michael Porter
        in his 1985 article “Competitive Advantage.” While these principles have been around for a
        long time, this article was the first time these principles had been articulated in a
        positioning matrix. This article, along with other ones released around this time, helped
        start the study and practice of Strategic Management in business.
      </P>

      <H1>Sources</H1>
      <Ul>
        <li>
          MacMillan, I., & McGrath, R. (1997, July). “Discovering the New Points of
          Differentiation”. Harvard Business Review.
          https://hbr.org/1997/07/discovering-new-points-of-differentiation
        </li>
        <li>
          Dyer, J., Godfrey, P., Jensen, R., Bryce, D. (2024, February). “Strategic Management:
          Concepts and Cases, 5th Edition”, Chapter 5, pp 81-97. Wiley.
          https://www.wiley.com/en-us/Strategic+Management%3A+Concepts+and+Cases%2C+5th+Edition-p-9781394161881
        </li>
        <li>
          Institute for Manufacturing. (2016). “Porter's Generic Competitive Strategies (ways of
          competing)”. University of Cambridge.
          https://www.ifm.eng.cam.ac.uk/research/dstools/porters-generic-competitive-strategies/
        </li>
        <li>Porter, M. (1985). "Competitive Advantage", Ch. 1, pp 11-15. The Free Press.</li>

        <li>Read The World's Oldest Known Customer Complaint To Copper Dealer Ea-nasir</li>
        <li>2024 Walmart Investor Report, Pages 8-9,11</li>
        <li>Ryanair-2024-Annual-Report.pdf, Pages 88-89</li>
      </Ul>

      <Updated date="2/21/25" />
    </div>
  );
}
